import React, { Fragment, useEffect, useState } from 'react'
import { Box, InputBase } from '@mui/material'
import { Select } from '../Select'
import TextFormatter from '../TextFormatter'
import { styled } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import Spinner from '../Spinner'

const StyledTextArea = styled(InputBase)(({ theme }) => ({
    width: '100% !important',
    '& .MuiInputBase-input': {
        borderRadius: '0.4rem',
        position: 'relative',
        backgroundColor: theme.palette.background.paper,
        border: '0.1rem solid #ebebed',
        fontSize: '1.4rem',
        padding: '1rem 1.2rem',
        lineHeight: 1.5,
        transition: theme.transitions.create(['border-color', 'box-shadow']),
        minHeight: '4rem',
        resize: 'vertical',
        '&:hover': {
            borderColor: `${theme.palette.primary.main} !important`,
        },
        '&:focus': {
            borderColor: theme.palette.primary.main,
            boxShadow: '0 0.2rem 0.4rem 0 rgba(48, 127, 193, 0.15)',
            backgroundColor: theme.palette.common.white,
            outline: 'none',
        },
        '&::placeholder': {
            color: '#b4b4b4',
            opacity: 1,
        },
        '&:disabled': {
            cursor: 'not-allowed',
            opacity: 0.6,
            backgroundColor: '#f5f5f5',
        },
    },
    '&.Mui-focused:not(.Mui-error) .MuiInputBase-input': {
        borderColor: `${theme.palette.primary.main} !important`,
        backgroundColor: theme.palette.common.white,
    },
    '&.Mui-error .MuiInputBase-input': {
        borderColor: '#d32f2f !important',
    },
    '&.MuiInputBase-multiline': {
        padding: 0,
    },
}))

const ErrorText = styled(Box)({
    color: '#d32f2f',
    fontSize: '1.2rem',
    marginTop: '0.4rem',
})

const CharacterCount = styled(Box)<{ hasError?: boolean }>(({ hasError }) => ({
    color: hasError ? '#d32f2f' : '#73767A',
    fontSize: '1.2rem',
    marginTop: '0.4rem',
    textAlign: 'right',
}))

export interface JustificationOption {
    id: string | number
    name: string
    value?: string | null
    localizedName?: string
    localKeyName?: string
    listItemValue?: string | null
    localizedItemValue?: string | null
}

export interface JustificationSelectorProps {
    options?: JustificationOption[]
    value?: string
    onChange: (value: string, isPredefined?: boolean) => void
    placeholder?: string
    multiline?: boolean
    maxCharacters?: number
    minCharacters?: number
    showCharacterCount?: boolean
    errorMessage?: string
    error?: boolean
    isLoading?: boolean
    getOptionLabel?: (option: JustificationOption) => string
    inputProps?: any
    onSubmit?: () => void
    onFocus?: () => void
    validate?: (value: string) => boolean | string
    disabled?: boolean
    otherOptionName?: string
    otherOptionLabel?: string
}

export const JustificationSelector: React.FC<JustificationSelectorProps> = ({
    options = [],
    value = '',
    onChange,
    placeholder = 'SelectJustification',
    multiline = true,
    maxCharacters,
    minCharacters,
    showCharacterCount = true,
    errorMessage,
    error: externalError,
    isLoading = false,
    getOptionLabel: customGetOptionLabel,
    inputProps,
    onSubmit,
    onFocus,
    validate,
    disabled = false,
    otherOptionName = 'Other',
    otherOptionLabel = 'OtherJustification',
}) => {
    const { t } = useTranslation()
    const [allOptions, setAllOptions] = useState<JustificationOption[]>([])
    const [selectedOption, setSelectedOption] = useState<JustificationOption | null>(null)
    const [validationError, setValidationError] = useState<string>('')

    const getOptionLabel = customGetOptionLabel || ((option: JustificationOption) => {
        // For predefined options, use translation of localKeyName
        if (option?.localKeyName && option.name !== otherOptionName) {
            // Remove the MyIdentity_ prefix to match the localization resource set
            const translationKey = option.localKeyName.replace(/^MyIdentity_/, '')
            return t(translationKey)
        }
        // For "Other" option, translate the name
        if (option.name === otherOptionName) {
            return t(option.localizedName || option.name)
        }
        return option?.name || option?.localizedName || ''
    })
    // Build options list with "Other" option
    useEffect(() => {
        const optionsWithOther: JustificationOption[] = [
            ...options,
            {
                id: 'other',
                name: otherOptionName, // Keep untranslated for comparisons
                value: otherOptionName,
                localizedName: t(otherOptionLabel),
            },
        ]
        setAllOptions(optionsWithOther)
    }, [options, otherOptionName, otherOptionLabel])

    // Set selected option based on value
    useEffect(() => {
        if (allOptions.length === 0) return

        if (!value) {
            // Default to "Other" if no value
            const otherOption = allOptions.find((o) => o.name === otherOptionName)
            setSelectedOption(otherOption || null)
        } else {
            // Strip MyIdentity_ prefix from value for comparison
            const normalizedValue = value.replace(/^MyIdentity_/, '')

            // Find matching predefined option by comparing normalized localKeyName
            const matchedOption = allOptions.find((o) => {
                if (!o.localKeyName) return false
                const normalizedLocalKey = o.localKeyName.replace(/^MyIdentity_/, '')
                return normalizedLocalKey === normalizedValue || o.localKeyName === value
            })

            if (matchedOption) {
                setSelectedOption(matchedOption)
            } else {
                // Custom text, select "Other"
                const otherOption = allOptions.find((o) => o.name === otherOptionName)
                setSelectedOption(otherOption || null)
            }
        }
    }, [allOptions, value, otherOptionName])

    // Validate textarea when "Other" is selected
    useEffect(() => {
        const isOtherSelected = selectedOption?.name === otherOptionName
        if (!isOtherSelected) {
            setValidationError('')
            return
        }

        const currentLength = value?.length || 0
        let error = ''

        if (minCharacters && currentLength < minCharacters) {
            error = `${t("MinLengthComment", { minLength: minCharacters })}`
        } else if (maxCharacters && currentLength > maxCharacters) {
            error = `${t("MaxLengthComment", { maxLength: maxCharacters })}`
        } else if (validate && value) {
            const customValidation = validate(value)
            if (typeof customValidation === 'string') {
                error = customValidation
            } else if (customValidation === false) {
                error = t("InvalidInput")
            }
        }

        setValidationError(error)
    }, [value, minCharacters, maxCharacters, validate, selectedOption, otherOptionName])

    const hasError = externalError || !!validationError
    const displayErrorMessage = errorMessage || validationError

    const handleDropdownChange = (selectedItem: JustificationOption) => {
        if (!selectedItem) {
            return
        }

        setSelectedOption(selectedItem)

        if (selectedItem.name === otherOptionName) {
            // Clear value when switching to "Other"
            onChange('', false)
        } else {
            // Emit localKeyName for predefined options (this is what backend expects)
            const valueToEmit = selectedItem.localKeyName || ''
            onChange(valueToEmit, true)
        }
    }

    const handleTextChange = (newValue: string) => {
        onChange(newValue, false)
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey && onSubmit && !hasError) {
            e.preventDefault()
            onSubmit()
        }
    }

    const isOtherSelected = selectedOption?.name === otherOptionName
    const showDropdown = allOptions.length > 0

    return (
        <Fragment>
            {showDropdown && selectedOption && (
                <Select
                    placeholder={placeholder}
                    options={allOptions}
                    isLoading={isLoading}
                    onChange={handleDropdownChange}
                    value={selectedOption}
                    getOptionLabel={getOptionLabel}
                    disabled={disabled}
                />
            )}

            {isOtherSelected && (
                <Fragment>
                    <Box
                        sx={{
                            width: '100%',
                            mt: showDropdown ? 1.5 : 0,
                        }}
                    >
                        <StyledTextArea
                            multiline={multiline}
                            value={value}
                            error={hasError}
                            onChange={(e) => handleTextChange(e.target.value)}
                            onFocus={onFocus}
                            onKeyDown={handleKeyDown}
                            disabled={disabled}
                            placeholder={t("AddAComment")}
                            minRows={2}
                            {...inputProps}
                        />

                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="flex-start"
                        >
                            {displayErrorMessage && (
                                <ErrorText>
                                    {displayErrorMessage}
                                </ErrorText>
                            )}
                            {!displayErrorMessage && <Box />}

                            {maxCharacters && showCharacterCount && (
                                <CharacterCount hasError={hasError}>
                                    {value?.length || 0}/{maxCharacters}
                                </CharacterCount>
                            )}
                        </Box>
                    </Box>
                </Fragment>
            )}
        </Fragment>
    )
}
// Separate component for displaying justification labels in read-only contexts (e.g., tables)
export interface JustificationLabelProps {
    value: string | null | undefined
    options?: JustificationOption[]
    isLoading?: boolean
    otherOptionName?: string
    maxCharacters?: number
    fontSize?: string | number
}

export const JustificationLabel: React.FC<JustificationLabelProps> = ({
    value,
    options = [],
    isLoading = false,
    otherOptionName = 'Other',
    maxCharacters = 80,
    fontSize = '13px',
}) => {
    const { t } = useTranslation()

    if (isLoading) {
        return <Spinner />
    }

    if (!value) {
        return <span>-</span>
    }

    // Strip MyIdentity_ prefix from value for comparison
    const normalizedValue = value.replace(/^MyIdentity_/, '')

    // Try to find a matching predefined option
    const matchedOption = options.find((o) => {
        if (!o.localKeyName) return false
        const normalizedLocalKey = o.localKeyName.replace(/^MyIdentity_/, '')
        return normalizedLocalKey === normalizedValue || o.localKeyName === value
    })

    let displayLabel = value
    if (matchedOption && matchedOption.name !== otherOptionName) {
        // Return translated predefined justification
        const translationKey = matchedOption.localKeyName!.replace(/^MyIdentity_/, '')
        displayLabel = t(translationKey)
    }

    return (
        <TextFormatter
            value={displayLabel}
            maxCharacters={maxCharacters}
            fontSize={fontSize}
        />
    )
}

export default JustificationSelector

